import React, { useEffect, useState } from "react"

import { Message } from "@locmod/intl"
import { openModal } from "@locmod/modal"
import { sendGTMEvent } from "@next/third-parties/google"
import cx from "classnames"
import {
  useReadContract,
  useWaitForTransactionReceipt,
  useWriteContract,
} from "wagmi"

import { buttonMessages } from "components/inputs"
import { Icon } from "components/ui"
import { isUserRejectedRequestError } from "helpers"
import { ConnectorName, useWallet } from "wallet"
import { isFeatureSupported } from "wallet/config/gasSponsorship"

import { useBalanceContext } from "src/contexts/BalanceContext"
import { useUserContext } from "src/contexts/user"

import { useDetailedSxBetslip } from "@/contexts/DetailedSxBetslipContext"
import useChainProviderHook from "@/hooks/useChainProvider"
import { useSXBetPlacement } from "@/SX/hooks/useSXBetPlacement"
import { getSelectedTokenSymbol } from "@/utils/helper"
import updateSxDiscordChannel from "src/helpers/updateSxDiscordChannel"

import messages from "./messages"
import { berachain } from "viem/chains"
import { parseAbi, maxUint256, encodeFunctionData, type Address } from "viem"
import { SX_CONFIG } from "@/SX/constants"
import { checkSystemStatus } from "@/SX/api"

interface SxBetButtonProps {
  isEnoughBalance: boolean
  isBalanceFetching: boolean
}

const abi = parseAbi([
  "function allowance(address owner, address spender) view returns (uint256)",
  "function approve(address spender, uint256 amount) returns (bool)",
])

const SxBetButton: React.FC<SxBetButtonProps> = ({
  isEnoughBalance,
  isBalanceFetching,
}) => {
  const { currentUser } = useUserContext()
  const { placeSXBet } = useSXBetPlacement()
  const [loading, setLoading] = useState(false)

  const { chainId } = useChainProviderHook()
  const { connector, account, chain } = useWallet()
  const { refetchBalances } = useBalanceContext()
  const { ticket, betAmount, payout, removeAll } = useDetailedSxBetslip()

  // ---- Contracts ----
  const { BET_TOKEN, PLACEBET } = SX_CONFIG[chainId].CONTRACTS
  // Get gas sponsorship functionality
  const { executeGaslessTransaction, hasGasSponsorship, isAAWallet } =
    useWallet()

  // Check if we can use gasless transactions for approvals on this chain
  // Only use gas sponsorship for AA wallets, not regular wallets
  const canUseApprovalSponsorship =
    chainId &&
    isFeatureSupported(chainId, "sponsorApprovals") &&
    hasGasSponsorship &&
    !!executeGaslessTransaction &&
    isAAWallet // Only use gas sponsorship for AA wallets

  // ---- Allowance check ----
  const { data: isAllowance } = useReadContract({
    address: BET_TOKEN,
    abi: abi,
    functionName: "allowance",
    args: [account, PLACEBET],
  })

  // ---- Approve flow ----
  const {
    writeContract: approveContract,
    data: approveHash,
    isPending: isApprovePending,
  } = useWriteContract()

  const {
    data: approveTxReceipt,
    isLoading: isApproveWaitForTransactionReceiptPending,
  } = useWaitForTransactionReceipt({ hash: approveHash })

  // ---- Place Bet flow ----
  const {
    writeContract,
    data: hash,
    isPending: isWriteContractPending,
  } = useWriteContract()

  const {
    data: txReceipt,
    isLoading: isWaitForTransactionReceiptPending,
    isError: isWaitForTransactionReceiptError,
  } = useWaitForTransactionReceipt({ hash })

  const selectedTokenSymbol = getSelectedTokenSymbol(chainId)

  const isApproveRequired =
    isAllowance !== undefined && Number(betAmount) > Number(isAllowance)

  const isLoading =
    loading ||
    isWriteContractPending ||
    isWaitForTransactionReceiptPending ||
    isApprovePending ||
    isApproveWaitForTransactionReceiptPending

  const isDisabled =
    !isEnoughBalance || isBalanceFetching || isLoading || !+betAmount

  // --- Approve Flow ---
  const handleApprove = async () => {
    // Set loading state immediately for gas-sponsored approvals
    if (canUseApprovalSponsorship) {
      setLoading(true)
    }

    try {
      // Try gas sponsorship first if available (only for AA wallets)
      if (canUseApprovalSponsorship && executeGaslessTransaction) {
        try {
          // Encode the approval function call
          const encodedData = encodeFunctionData({
            abi: abi,
            functionName: "approve",
            args: [PLACEBET, maxUint256],
          })

          const txHash = await executeGaslessTransaction(
            BET_TOKEN as Address,
            encodedData,
            BigInt(0)
          )

          setTimeout(() => {
            handleBet()
          }, 1000)

          return txHash
        } catch (error) {
          setLoading(false)
        }
      }

      approveContract({
        address: BET_TOKEN,
        abi: abi,
        functionName: "approve",
        args: [PLACEBET, maxUint256],
      })
    } catch (error) {
      if (canUseApprovalSponsorship) {
        setLoading(false)
      }
    }
  }

  // --- Bet Flow ---
  const handleBet = async () => {
    if (!ticket || ticket.length === 0) {
      return
    }
    const { isMakerBettingOutcomeOne, allOdds, marketHash } = ticket[0] // SX only supports single bets
    setLoading(true)
    try {
      sendGTMEvent({
        event: "betslip_bet_initiated",
        user_id: currentUser?.id,
        bet_type: "Single",
        token_approval_required: isApproveRequired,
        bet_count: 1,
        bet_amount: betAmount,
        bet_payout: payout,
        destination_chain: "Berachain",
        destination_token: selectedTokenSymbol,
        destination_wallet_address: account,
        wallet_app_name:
          connector?.name === ConnectorName.Privy ? "privy" : connector?.name,
      })

      const txHash = await placeSXBet({
        amount: betAmount,
        bettingOnOutcomeOne: !isMakerBettingOutcomeOne,
        allOdds: allOdds,
        writeContract: writeContract,
        marketHash,
      })

      // Check if this was a gas-sponsored transaction
      // Gas-sponsored transactions return a hash but wagmi's hash state remains undefined
      if (txHash && !hash) {
        // Trigger success flow manually for gas-sponsored transactions

        try {
          // Update Discord channel for SX bet
          await updateSxDiscordChannel(
            ticket,
            betAmount,
            payout / Number(betAmount), // Calculate total odds from payout
            chain,
            txHash,
            selectedTokenSymbol,
            // selectedTokenSymbol,
            false // SX bets are not live by default
          )
        } catch (error) {
          console.error("Failed to update Discord channel for SX bet:", error)
          // Don't fail the entire flow if Discord update fails
        }

        // Send success analytics
        sendGTMEvent({
          event: "betslip_bet_completed",
          user_id: currentUser?.id,
          bet_type: "Single",
          token_approval_required: isApproveRequired,
          bet_count: 1,
          bet_amount: betAmount,
          bet_payout: payout,
          destination_chain:
            chainId === berachain.id ? "Berachain" : "Arbitrum Sepolia",
          destination_token: selectedTokenSymbol,
          destination_wallet_address: account,
          wallet_app_name:
            connector?.name === ConnectorName.Privy ? "privy" : connector?.name,
        })

        // Show success modal
        openModal("SuccessModal", {
          title: "Bet placed successfully!",
        })

        // Clear betslip after successful bet placement
        removeAll()

        // Refetch balances after successful bet placement
        refetchBalances()
      }
    } catch (error) {
      console.error("Error placing SX bet:", error)
      if (!isUserRejectedRequestError(error)) {
        openModal("ErrorModal", {
          title: "Failed to place the bet",
        })
      }

      if (isUserRejectedRequestError(error)) {
        sendGTMEvent({
          event: "place_bet_rejected",
          destination_chain: "Berachain",
          destination_wallet_address: account,
        })
      }
    } finally {
      setLoading(false)
    }
  }

  // --- Action Wrapper ---
  const handleAction = async () => {
    if (!ticket?.length) {
      return
    }

    const statusIsUp = await checkSystemStatus()
    if (!statusIsUp) {
      openModal("ErrorModal")
      return
    }

    if (isApproveRequired) {
      return handleApprove()
    }

    await handleBet()
  }

  // Bet after approval
  useEffect(() => {
    if (approveTxReceipt) {
      handleBet()
    }
  }, [approveTxReceipt])

  // Handle bet success / errors
  useEffect(() => {
    const handleDiscordUpdate = async () => {
      if (!txReceipt) {
        return
      }

      // Update Discord channel for SX bet
      try {
        await updateSxDiscordChannel(
          ticket,
          betAmount,
          payout / Number(betAmount), // Calculate total odds from payout
          chain,
          txReceipt.transactionHash,
          selectedTokenSymbol,
          // selectedTokenSymbol,
          false // SX bets are not live by default
        )
      } catch (error) {
        console.error("Failed to update Discord channel for SX bet:", error)
        // Don't fail the entire flow if Discord update fails
      }
    }

    if (txReceipt) {
      handleDiscordUpdate()
      sendGTMEvent({
        event: "betslip_bet_completed",
        user_id: currentUser?.id,
        bet_type: "Single",
        token_approval_required: isApproveRequired,
        bet_count: 1,
        bet_amount: betAmount,
        bet_payout: payout,
        destination_chain: "Berachain",
        destination_token: selectedTokenSymbol,
        destination_wallet_address: account,
        wallet_app_name:
          connector?.name === ConnectorName.Privy ? "privy" : connector?.name,
      })

      openModal("SuccessModal", {
        title: "Bet placed successfully!",
      })
      // Clear betslip after successful bet placement
      removeAll()
      // Refetch balances after successful bet placement
      refetchBalances()
    }
    if (isWaitForTransactionReceiptError) {
      openModal("ErrorModal", {
        title: "Failed to place SX bet",
      })
      if (isUserRejectedRequestError(isWaitForTransactionReceiptError)) {
        sendGTMEvent({
          event: "place_bet_rejected",
          destination_chain:
            chainId === berachain.id ? "Berachain" : "Arbitrum Sepolia",
          destination_wallet_address: account,
        })
      }
    }
  }, [txReceipt, isWaitForTransactionReceiptError])

  const rootClassName = cx(
    "flex items-center justify-between py-1 pr-1 border rounded-sm w-full",
    isDisabled
      ? "bg-accent-grey-900 border-accent-grey-950 cursor-not-allowed text-accent-grey-700"
      : "bg-brand-light-500 text-black border-white/20"
  )

  const possibleWinClassName = cx(
    "text-caption-12 flex items-center p-2 rounded-min flex-none select-none",
    isDisabled
      ? "bg-accent-grey-800 text-accent-grey-700"
      : "bg-accent-grey-1000/60 text-white"
  )

  return (
    <button
      className={rootClassName}
      onClick={() => void handleAction()}
      disabled={isDisabled}
    >
      <div className="w-full text-left pl-4 px-1">
        {isLoading ? (
          <Icon className="size-4 mx-auto" name="interface/spinner" />
        ) : (
          <Message
            className="font-bold text-caption-14"
            value={
              isApproveRequired
                ? buttonMessages.approve
                : buttonMessages.placeBet
            }
          />
        )}
      </div>
      <div className={possibleWinClassName}>
        <Message className="mr-1" value={messages.possibleWin} />
        <div className="font-semibold">
          {(Math.floor(Number(payout) * 100) / 100).toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
          {selectedTokenSymbol}
        </div>
      </div>
    </button>
  )
}
export default SxBetButton
